import { useState } from 'react';
import { SimpleAlert } from '../../../../../rbts/helpers';
import {
  getBalanceRecovery,
  getHistorialRecargas,
  getMovimientos,
  getWalletUsersByNombre,
} from '../../../../service/enlistment';
import { ReporteCARow, ClienteInfoCA } from '../models/ReporteCAModel';

export type SearchTypeCA = 'folio' | 'telefono' | 'nombre';

const buildRows = (recargas: any[], movimientos: any[], folio: string): ReporteCARow[] => {
  const cargaRows: ReporteCARow[] = (Array.isArray(recargas) ? recargas : []).map((r) => ({
    folio: r.folio || folio,
    dia_label: r.dia_administrativo || '',
    id_dia: null,
    tipo: 'CARGA',
    descripcion: r.tipo_recarga || '',
    monto: Math.abs(Number(r.recarga) || 0),
    cantidad: null,
  }));

  const abonoRows: ReporteCARow[] = (Array.isArray(movimientos) ? movimientos : []).map((m) => ({
    folio: m.folio || folio,
    dia_label: m.fecha || '',
    id_dia: m.id_dia || null,
    tipo: 'ABONO',
    descripcion: [m.punto_venta_nombre, m.producto_nombre].filter(Boolean).join(' — '),
    monto: Math.abs(Number(m.costo_total) || 0),
    cantidad: Number(m.cantidad) || null,
  }));

  return [...cargaRows, ...abonoRows];
};

const fetchForFolio = async (folio: string): Promise<ReporteCARow[]> => {
  const [recargas, movimientos] = await Promise.all([
    getHistorialRecargas(folio).catch(() => []),
    getMovimientos(folio).catch(() => []),
  ]);
  return buildRows(recargas, movimientos, folio);
};

export const useReporteCA = () => {
  const [rows, setRows] = useState<ReporteCARow[]>([]);
  const [clienteInfoList, setClienteInfoList] = useState<ClienteInfoCA[]>([]);
  const [loading, setLoading] = useState(false);

  const search = async (type: SearchTypeCA, value: string) => {
    if (!value.trim()) return;
    setLoading(true);
    setRows([]);
    setClienteInfoList([]);

    try {
      if (type === 'folio' || type === 'telefono') {
        const filter =
          type === 'folio'
            ? { folio: value, telefono_format: '' }
            : { folio: '', telefono_format: value };

        let balance: any = null;
        try {
          balance = await getBalanceRecovery(filter);
        } catch {
          // balance not found — continue with direct folio fetch
        }

        const clienteInfo: ClienteInfoCA = {
          nombre: balance?.cliente?.nombre || null,
          telefono: balance?.cliente?.telefono || null,
          ticketsActivos: (balance?.ticketsActivos || []).map((t: any) => ({
            folio: t.folio,
            saldo: t.saldo,
          })),
          ticketsCancelados: (balance?.ticketsCancelados || []).map((t: any) => ({
            folio_anterior: t.folio_anterior,
            folio_nuevo: t.folio_nuevo,
          })),
        };
        setClienteInfoList([clienteInfo]);

        const allFolios = new Set<string>();
        (balance?.ticketsActivos || []).forEach((t: any) => {
          if (t.folio) allFolios.add(t.folio);
        });
        (balance?.ticketsCancelados || []).forEach((t: any) => {
          if (t.folio_anterior) allFolios.add(t.folio_anterior);
          if (t.folio_nuevo) allFolios.add(t.folio_nuevo);
        });
        if (allFolios.size === 0 && type === 'folio') allFolios.add(value);

        const allRows = (
          await Promise.all(Array.from(allFolios).map(fetchForFolio))
        ).flat() as ReporteCARow[];
        setRows(allRows);
      } else {
        // nombre: opción B — mostrar todos los resultados combinados
        const matches = await getWalletUsersByNombre(value);
        if (!matches || matches.length === 0) {
          SimpleAlert('No se encontraron clientes con ese nombre', 3000, 'error');
          return;
        }

        const withFolio = matches.filter((m: any) => m.folio);
        if (withFolio.length === 0) {
          SimpleAlert('Los clientes encontrados no tienen folio asignado', 3000, 'error');
          return;
        }

        const allRows = (
          await Promise.all(withFolio.map((m: any) => fetchForFolio(m.folio)))
        ).flat() as ReporteCARow[];
        setRows(allRows);

        setClienteInfoList(
          withFolio.map((m: any) => ({
            nombre: m.nombre || null,
            telefono: null,
            ticketsActivos: [{ folio: m.folio, saldo: '' }],
            ticketsCancelados: [],
          }))
        );
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || 'Error al obtener los datos';
      SimpleAlert(message, 3000, 'error');
    } finally {
      setLoading(false);
    }
  };

  return { rows, clienteInfoList, loading, search };
};
